import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faCheck, faExclamation, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { faRepeat } from "@fortawesome/pro-solid-svg-icons";

import { ListPerson } from "../../../../../schema/list";
import { CopywritingCampaign } from "../../../../../pages/ListEditCopywriting/ListEditCopywriting";
import { getCampaignTagState } from "../utils/getCampaignTagState";
import { AiSnippetStatus } from "../utils/getAiSnippetStatus";
import { InfoTag } from "./InfoTag";
import { CampaignTag } from "./CampaignTag";
import { AiSnippetStatusIcon } from "./AiSnippetStatusIcon";
import { CreateCampaignButton } from "./CreateCampaignButton";

interface ReviewCampaignTagProps {
	row: ListPerson;
	/** The campaign the card resolved for the lead (see `resolveLeadCampaignConfig`). */
	selectedCampaignConfig: CopywritingCampaign | null | undefined;
	allowCampaignChange: boolean;
	onAssignClick?: () => void;
	aiSnippetStatus: AiSnippetStatus | null;
	/** Tracker lists render "Create campaign" as a secondary action. */
	isTrackerList: boolean;
}

/**
 * The campaign a lead waiting in Review to send will go out with — pre-assigned, not yet
 * assigned, or no campaign created — with the AI-snippet state beside it. Shown only while the
 * lead has no schedules yet: once it is sent, the step tags take over. Extracted from `Lead.tsx`
 * so the compact Review-to-send card (PREC-4175) and the list-row card render one tag.
 */
export const ReviewCampaignTag: FunctionComponent<ReviewCampaignTagProps> = ({
	row,
	selectedCampaignConfig,
	allowCampaignChange,
	onAssignClick,
	aiSnippetStatus,
	isTrackerList,
}) => {
	const { t } = useTranslation();

	if (!row.schedules || row.schedules.length > 0) {
		return null;
	}

	if (row.lemlist?.id) {
		return <InfoTag icon={faExclamation} label={row.lemlist.id.toString()} style="success" />;
	}

	const campaignTagState = getCampaignTagState(selectedCampaignConfig);

	if (campaignTagState === "no-campaign-created") {
		return <CreateCampaignButton isTrackerList={isTrackerList} row={row} />;
	}

	const isPreAssigned = campaignTagState === "pre-assigned" && !!selectedCampaignConfig;

	return (
		<div className="w-fit max-w-[300px] inline-flex items-center gap-1">
			<CampaignTag
				allowCampaignChange={allowCampaignChange}
				hoverIcon={isPreAssigned ? faRepeat : faPlus}
				hoverLabel={isPreAssigned ? "Change campaign" : "Add to campaign"}
				icon={isPreAssigned ? faCheck : faExclamation}
				label={
					isPreAssigned
						? t("messages.preAssignedToCampaign", { name: selectedCampaignConfig.name })
						: t("messages.notYetPreAssigned")
				}
				row={row}
				style={isPreAssigned ? "success" : "info"}
				onAssignClick={onAssignClick}
			/>
			{aiSnippetStatus && <AiSnippetStatusIcon status={aiSnippetStatus} />}
		</div>
	);
};
